/**
 * Browser mirror of `vc_be/services/whatsappFlowEngine.js`.
 *
 * The device preview is only worth having if it is wrong in no way that matters,
 * so `norm`, `matchOption` and `renderStep` are ported line for line rather than
 * re-derived — a preview that accepts a reply the server rejects is worse than
 * no preview, because it is believed.
 *
 * What is deliberately NOT mirrored, and why it cannot change what the customer
 * sees in a single sitting:
 *   · persistence (`_setState`)   — the simulator holds the step in React state
 *   · expiry (`expiresAt`)        — a real abandon takes `expiryMinutes`; Restart
 *                                   is the same thing without the wait
 *   · action side effects         — surfaced as a note, never run. An opt-out
 *                                   run from a preview is a real opt-out.
 *   · MAX_CHAINED_STEPS           — unreachable: `_advance` never recurses, a
 *                                   step with options always waits for a reply.
 *
 * Everything returns an ordered `items` array — the exact sequence the contact
 * would receive — so bubble order can never drift from the server's send order.
 */

/** Normalised comparison form: lowercase, collapsed whitespace, no edge punctuation. */
export const norm = (s) => String(s || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .replace(/^[^\w-￿]+|[^\w-￿]+$/g, '');

/**
 * Which option did the customer pick? Bare number, number with punctuation
 * ("2.", "option 2"), or the option's own text — exact first, then a prefix that
 * fits exactly one option. Never fuzzy.
 *
 * @returns {number} zero-based option index, or -1
 */
export function matchOption(input, options = []) {
  const raw = String(input || '').trim();
  if (!raw || !options.length) return -1;

  const numMatch = raw.match(/^\D{0,10}?(\d{1,2})\b/);
  if (numMatch) {
    const n = Number.parseInt(numMatch[1], 10);
    if (n >= 1 && n <= options.length) return n - 1;
    // A number outside the range is still a *numeric* answer — treating it as
    // text would then prefix-match some unrelated label.
    return -1;
  }

  const n = norm(raw);
  if (!n) return -1;

  const exact = options.findIndex((o) => norm(o.label) === n);
  if (exact >= 0) return exact;

  const prefixed = options.filter((o) => norm(o.label).startsWith(n));
  if (prefixed.length === 1) return options.indexOf(prefixed[0]);

  return -1;
}

/** Render a step as the numbered text WhatsApp shows verbatim. */
export function renderStep(step) {
  const lines = [String(step?.message || '').trim()];
  (step?.options || []).forEach((o, i) => lines.push(`${i + 1}. ${o.label || `Option ${i + 1}`}`));
  return lines.filter(Boolean).join('\n');
}

export const stepByKey = (flow, key) => (flow?.steps || []).find((s) => s.key === key) || null;

/** Would this text start the flow? Mirrors the candidate scan in `handleInbound`. */
export function matchTrigger(flow, text, { isFirstMessage = false } = {}) {
  if (!flow || flow.triggerType === 'manual') return false;
  if (flow.triggerType === 'first_message') return isFirstMessage;
  const n = norm(text);
  return (flow.keywords || []).some((k) => {
    const kn = norm(k);
    return kn && (n === kn || n.includes(kn));
  });
}

const msg = (text) => ({ kind: 'msg', text });
const note = (text, tone = 'info') => ({ kind: 'note', tone, text });

/**
 * Send `nextKey` and report where the contact lands — the mirror of `_advance`.
 * @returns {{ items: Array, step: string|null, ended: boolean, reason: string }}
 */
export function advance(flow, nextKey) {
  if (!nextKey) {
    return { items: [], step: null, ended: true, reason: 'flow_complete' };
  }

  const step = stepByKey(flow, nextKey);
  if (!step) {
    // The server logs this and drops the contact out of the flow having sent
    // nothing — silence is the symptom, so the preview has to name it.
    return {
      items: [note(`Points at “${nextKey}”, which no longer exists. The flow stops here and the contact receives nothing.`, 'error')],
      step: null,
      ended: true,
      reason: 'missing_step',
    };
  }

  const body = renderStep(step);
  if (!body.trim()) {
    // renderStep of an empty step is an empty string, and WhatsApp rejects an
    // empty message — so nothing arrives, however the flow continues.
    return {
      items: [note('This step has no text and no options — WhatsApp rejects an empty message, so nothing is delivered.', 'error')],
      step: null,
      ended: true,
      reason: 'empty_step',
    };
  }

  const items = [msg(body)];

  // A step with no options is terminal — there is nothing to reply to.
  if (!step.options?.length) {
    return { items, step: null, ended: true, reason: 'terminal_step' };
  }

  return { items, step: step.key, ended: false, reason: '' };
}

/**
 * One inbound reply against the step the contact is on — the mirror of the
 * "continue an in-progress flow" branch of `handleInbound`.
 *
 * @param {object} flow
 * @param {string} stepKey   step the contact is currently on
 * @param {string} text      what they typed
 * @param {object} [opts]
 * @param {Record<string, object>} [opts.actionByKey]  action registry, for labels
 */
export function replyTo(flow, stepKey, text, { actionByKey = {} } = {}) {
  const step = stepByKey(flow, stepKey);
  if (!step) {
    return {
      items: [note('The step this contact was on has been deleted — the flow drops them silently.', 'error')],
      step: null,
      ended: true,
      reason: 'step_gone',
      optionIndex: -1,
    };
  }

  const idx = matchOption(text, step.options);
  if (idx < 0) {
    // The menu is re-sent under the notice, so the options are always in reach.
    return {
      items: [msg(`${flow.invalidReplyMessage || ''}\n\n${renderStep(step)}`.trim())],
      step: stepKey,
      ended: false,
      reason: 'invalid_option',
      optionIndex: -1,
    };
  }

  const option = step.options[idx];
  const items = [];

  if (option.action) {
    const def = actionByKey[option.action];
    if (!def) {
      items.push(note(`Runs “${option.action}” — no such action exists any more, so it is skipped.`, 'warn'));
    } else if (def.principal !== 'phone') {
      // Rule 1 in the engine: a flow cannot prove the sender is a given admin.
      items.push(note(`“${def.label}” needs an admin, so the server refuses to run it from a flow.`, 'error'));
    } else {
      items.push(note(`Runs ${def.label}${option.actionTargetId ? ` · ${option.actionTargetId}` : ''} — not run here.`, 'action'));
    }
  }

  if (option.reply?.trim()) items.push(msg(option.reply.trim()));

  const next = advance(flow, option.next);
  return {
    items: [...items, ...next.items],
    step: next.step,
    ended: next.ended,
    reason: next.reason,
    optionIndex: idx,
  };
}
